import React, { useContext, useMemo } from "react";
import {
  Box,
  Typography,
  useTheme,
  IconButton,
  Tooltip,
} from "@mui/material";
import { NextPlanOutlined } from "@mui/icons-material";
import { useHistory } from "react-router-dom/cjs/react-router-dom";
import CloudProviderMark from "../../../components/Common/CloudProviderMark";
import { useAccessibleUserAccounts } from "../../../components/Common/HelperComponents/CustomHooks";
import {
  CloudProviderContext,
  getAccountCloudType,
} from "../../../CloudProviderContext";

/** Matches FinOps / cost value green on dashboard cards */
const COUNT_COLOR = "#228B22";

/**
 * Compact dashboard card: account count for the selected cloud provider.
 * Static (not draggable). Click / go-to opens Configuration → Accounts.
 */
const AccountsCard = () => {
  const theme = useTheme();
  const history = useHistory();
  const { selectedProvider } = useContext(CloudProviderContext);
  const accessibleAccounts = useAccessibleUserAccounts();

  const accountCount = useMemo(
    () =>
      accessibleAccounts.filter(
        (account) => getAccountCloudType(account) === selectedProvider
      ).length,
    [accessibleAccounts, selectedProvider]
  );

  const handleGoTo = () => {
    history.push("/configuration/accounts");
  };

  return (
    <Box
      role="button"
      tabIndex={0}
      aria-label="Number of Accounts — open Configuration"
      onClick={handleGoTo}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handleGoTo();
        }
      }}
      sx={{
        backgroundColor: theme.palette.background.default,
        borderRadius: "8px",
        boxShadow: "0 0 10px rgba(0,0,0,0.1)",
        padding: "8px 12px",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        cursor: "pointer",
        transition: "box-shadow 0.15s ease",
        "&:hover": {
          boxShadow: "0 0 14px rgba(0,0,0,0.14)",
        },
      }}
    >
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        width="100%"
        sx={{
          mb: 1,
          paddingBottom: "2px",
          borderBottom: `1px solid`,
        }}
      >
        <Box display="flex" alignItems="center" minWidth={0} flex={1}>
          <CloudProviderMark />
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: "bold",
              color: "text.primary",
              fontSize: "0.875rem",
              lineHeight: 1.2,
            }}
          >
            Number of Accounts
          </Typography>
        </Box>
        <Tooltip title="Open Configuration">
          <IconButton
            aria-label="go-to-configuration"
            size="small"
            sx={{
              p: 0,
              ml: 0.5,
              color: "text.secondary",
              transform: "rotate(-45deg)",
              flexShrink: 0,
            }}
            onClick={(e) => {
              e.stopPropagation();
              handleGoTo();
            }}
          >
            <NextPlanOutlined fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      <Box
        sx={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: 0,
          width: "100%",
        }}
      >
        <Typography
          variant="h4"
          sx={{
            fontWeight: "bold",
            color: COUNT_COLOR,
            fontVariantNumeric: "tabular-nums",
            lineHeight: 1.1,
            fontSize: { xs: "1.75rem", sm: "2rem" },
            textAlign: "center",
          }}
        >
          {accountCount}
        </Typography>
      </Box>
    </Box>
  );
};

export default AccountsCard;
